import { useIntl } from 'react-intl';
import Head from 'next/head';
import { useRouter } from 'next/router';

const SITE_URL = 'https://discordsuperman.xyz';

interface AppHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  structuredData?: any[];
}

export function AppHead(props: AppHeadProps) {
  const intl = useIntl();
  const router = useRouter();

  const defaultTitle = intl.formatMessage({
    defaultMessage: 'Discord自动聊天机器人 - Discord智能聊天助手',
    id: 'app.default.title',
  });
  const defaultDesc = intl.formatMessage({
    defaultMessage:
      '使用基于先进AI大模型驱动的Discord自动聊天机器人，轻松活跃您的群组气氛。提供智能对话、实时响应和个性化互动，提升社区参与度和用户体验。适用于各种Discord群组，助您打造充满活力的在线社区。',
    id: 'app.default.description',
  });
  const defaultKeywords = intl.formatMessage({
    defaultMessage:
      'Discord机器人,Discord自动聊天,AI聊天机器人,Discord助手,群组管理,社区互动,智能对话,Discord工具,聊天自动化,Discord SuperMan',
    id: 'app.default.keywords',
  });

  const siteTitle = props.title || defaultTitle;
  const siteDesc = props.description || defaultDesc;
  const siteKeywords = props.keywords || defaultKeywords;
  const siteImage = props.image || '/assets/discord-superman-logo.png';
  const siteType = props.type || 'website';

  // 推荐链接页 /buy/[referralCode] 与 /buy 内容相同，规范到 /buy
  const canonicalPath =
    router.pathname === '/buy/[referralCode]' ? '/buy' : router.pathname;
  const isReferralPage = canonicalPath !== router.pathname;

  // 默认语言（zh）不带前缀，其他语言带 /en、/zh-TW 前缀
  const localeUrl = (locale?: string) => {
    const prefix = locale === router.defaultLocale ? '' : `/${locale}`;
    if (canonicalPath === '/') return `${SITE_URL}${prefix || '/'}`;
    return `${SITE_URL}${prefix}${canonicalPath}`;
  };
  const canonicalUrl = localeUrl(router.locale);
  const showAlternates = !props.noIndex && !isReferralPage;

  return (
    <Head>
      <title>{siteTitle}</title>
      <meta name="description" content={siteDesc} />
      <meta name="keywords" content={siteKeywords} />
      <meta name="author" content="Discord SuperMan" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Language" content={router.locale || 'zh'} />

      {/* Canonical URL */}
      {!props.noIndex && <link rel="canonical" href={canonicalUrl} />}

      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/assets/discord-superman-logo.png"
      />

      {/* Open Graph */}
      <meta property="og:type" content={siteType} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={siteDesc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta
        property="og:image"
        content={`https://discordsuperman.xyz${siteImage}`}
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={siteTitle} />
      <meta
        property="og:site_name"
        content={intl.formatMessage({
          defaultMessage: 'Discord 智能聊天助手',
          id: 'title',
        })}
      />
      <meta
        property="og:locale"
        content={
          router.locale === 'zh'
            ? 'zh_CN'
            : router.locale === 'zh-TW'
            ? 'zh_TW'
            : 'en_US'
        }
      />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@DiscordSuperMan" />
      <meta name="twitter:creator" content="@DiscordSuperMan" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={siteDesc} />
      <meta
        name="twitter:image"
        content={`https://discordsuperman.xyz${siteImage}`}
      />

      {/* 多语言支持：指向当前页面的各语言版本 */}
      {showAlternates &&
        router.locales?.map((locale) => (
          <link
            key={locale}
            rel="alternate"
            hrefLang={locale}
            href={localeUrl(locale)}
          />
        ))}
      {showAlternates && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={localeUrl(router.defaultLocale)}
        />
      )}

      {/* 防止索引（如果需要） */}
      {props.noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* 结构化数据 - 由页面传入 */}
      {props.structuredData &&
        props.structuredData.map((data, index) => (
          <script
            key={index}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(data),
            }}
          />
        ))}

      {/* DNS预解析 */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.clarity.ms" />

      {/* 预加载关键资源 */}
      <link rel="preload" href="/assets/discord-superman-logo.png" as="image" />
    </Head>
  );
}
